// src/components/Sidebar.js

import React, { useState, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import makeStyles from '@mui/styles/makeStyles';
import { Link, useLocation } from "react-router-dom";

import FolderIcon from "@mui/icons-material/Folder";
import DescriptionIcon from "@mui/icons-material/Description";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HomeIcon from "@mui/icons-material/Home";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import DrawIcon from "@mui/icons-material/Draw";
import LayersIcon from "@mui/icons-material/Layers";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import { usePermisos } from "../utils/usePermisos";
import AcercaDeDialog from './AcercaDeDialog';
import { panelFirmaAccesoJWT, cargaMasivaMiCountFirmasPendientesJWT, dashboardEjecutivoAccesoJWT, honorariosAccesoJWT } from "../api/endpoints";
import InsightsIcon from "@mui/icons-material/Insights";
import TimelineIcon from "@mui/icons-material/Timeline";
import OutboxIcon from "@mui/icons-material/Outbox";
import FactCheckIcon from "@mui/icons-material/FactCheck";

import LogoExpandido from "../assets/sigifSinTexto.svg";
import LogoContraido from "../assets/isotipo.svg";

const drawerWidth = 220;
const drawerClosedWidth = 56;

const useStyles = makeStyles((theme) => ({
    drawerPaper: props => ({
        width: props.menuOpen ? drawerWidth : drawerClosedWidth,
        height: "100vh",
        transition: "width 0.3s cubic-bezier(.4,0,.2,1)",
        // El layout es flex column: header (logo) → lista (flex 1, scroll) → footer.
        // Sin esto el footer "Fiscalía de Estado" quedaba en position: absolute al
        // fondo del contenedor de menú, que cuando es más alto que el viewport
        // aparecía flotando en el medio en pantallas chicas (ej. 1366×768).
        display: "flex",
        flexDirection: "column",
        overflowX: "hidden",
        overflowY: "hidden",
        zIndex: 1200,
        background: "#f6f8fc",
        borderRight: `1px solid ${theme.palette.divider}`,
        whiteSpace: "nowrap",
        position: "fixed",
        left: 0,
        top: 0,
        boxSizing: "border-box"
    }),
    toolbar: theme.mixins.toolbar,
    logoBox: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: 76,
        width: "100%",
        paddingTop: 12,
        paddingBottom: 8,
        boxSizing: "border-box",
        background: "#e3eaf5",
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    active: {
        background: "#e3eaf5",
        "& .MuiListItemIcon-root": { color: theme.palette.primary.main },
    },
    // NUEVO: menos espacio entre icono y texto
    listItemIcon: {
        minWidth: 32,                // antes 56
        marginRight: theme.spacing(1) // opcional
    },
    nested: {
        paddingLeft: theme.spacing(4),
    },
    bottom: {
        flexShrink: 0,
        width: "100%",
        fontSize: 12,
        color: "#89a",
        padding: theme.spacing(1),
        background: "#e3eaf5",
        textAlign: "center",
        borderTop: `1px solid ${theme.palette.divider}`,
    },
    listScroll: {
        flex: 1,
        minHeight: 0, // permite shrink en flex container
        overflowY: "auto",
        overflowX: "hidden",
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: 2 },
    },
    listText: {
        transition: "opacity 0.3s",
        opacity: props => (props.menuOpen ? 1 : 0),
        width: props => (props.menuOpen ? "auto" : 0),
        whiteSpace: "nowrap",
        overflow: "hidden"
    }
}));

const Sidebar = ({ usuario, moduloActivo, setModuloActivo, menuOpen }) => {
    const classes = useStyles({ menuOpen });
    const location = useLocation();
    const { tienePermiso } = usePermisos();
    const [tienePanelFirma, setTienePanelFirma] = useState(false);
    const [firmasAbogadoPendientes, setFirmasAbogadoPendientes] = useState(0);
    const [esAbogadoApoderado, setEsAbogadoApoderado] = useState(false);
    // Arranca en true para no parpadear ocultando el menú a un abogado mientras carga.
    const [tieneHonorarios, setTieneHonorarios] = useState(true);
    // Version del bundle que el navegador tiene cargado. Se pide en runtime (no viene
    // incrustada) para poder detectar que el usuario esta con una copia cacheada vieja.
    const [versionFront, setVersionFront] = useState(null);
    const [acercaDeAbierto, setAcercaDeAbierto] = useState(false);
    // Esta copia del bundle lleva grabado su id de build; el servidor publica el suyo en
    // version.json. Si difieren, el navegador esta sirviendo algo viejo.
    const bundleViejo = !!process.env.REACT_APP_BUILD_ID && !!versionFront?.buildId
        && process.env.REACT_APP_BUILD_ID !== versionFront.buildId;
    const [tieneDashboardEjecutivo, setTieneDashboardEjecutivo] = useState(false);

    // Es abogado por función: abogado/fiscal/letrado/procurador. "Mi Actividad"
    // se muestra solo a estos usuarios — para el resto no aporta valor.
    // Requiere id_personal real (>0); sin vinculación a Personal no puede tener expedientes.
    const tienePersonalReal = usuario?.id_personal && Number(usuario.id_personal) > 0;
    const esAbogado = !!(tienePersonalReal && usuario?.funcion && (
        usuario.funcion.toLowerCase().includes("abogad") ||
        usuario.funcion.toLowerCase().includes("fiscal") ||
        usuario.funcion.toLowerCase().includes("letrad") ||
        usuario.funcion.toLowerCase().includes("procurad")
    ));

    useEffect(() => {
        panelFirmaAccesoJWT()
            .then(r => { if (r?.ok) setTienePanelFirma(r.esFiscal || r.esDelegado); })
            .catch(() => {});
        // Count de firmas de demanda pendientes + flag de apoderado en algún lote.
        // El botón "Panel de Firma Abogado" debe aparecer siempre a quienes son
        // apoderados, aunque ahora no tengan firmas pendientes.
        cargaMasivaMiCountFirmasPendientesJWT()
            .then(r => {
                if (r?.ok) {
                    setFirmasAbogadoPendientes(r.count || 0);
                    setEsAbogadoApoderado(!!r.esApoderado);
                }
            })
            .catch(() => {});
        // ¿Tiene honorarios propios? El permiso lo tienen 130 usuarios pero solo ~95
        // cobran; al resto el menú les abría una pantalla vacía.
        honorariosAccesoJWT()
            .then(r => { if (r?.ok) setTieneHonorarios(!!r.tieneHonorarios); })
            .catch(() => {});
        // Version del build. El ?t= evita que el navegador sirva un version.json viejo
        // justo cuando lo que queremos saber es si tiene algo viejo.
        fetch(`${process.env.PUBLIC_URL || ''}/version.json?t=${Date.now()}`)
            .then(r => (r.ok ? r.json() : null))
            .then(setVersionFront)
            .catch(() => {});
        // Acceso al Dashboard Ejecutivo (Fiscal + delegados)
        dashboardEjecutivoAccesoJWT()
            .then(r => { if (r?.ok) setTieneDashboardEjecutivo(!!r.acceso); })
            .catch(() => {});
    }, []);

        // Menú ordenado según lo solicitado
    const menuStructure = [
        { key: "inicio", label: "Inicio", path: "/", icon: <HomeIcon /> },
        // Dashboard Ejecutivo: visible solo al Fiscal y a sus delegados.
        {
            key: "dashboard-ejecutivo",
            label: "Dashboard Ejecutivo",
            path: "/dashboard-ejecutivo",
            icon: <InsightsIcon />,
            oculto: !tieneDashboardEjecutivo,
        },
        { key: "divider1", isDivider: true },
        { key: "expedientes", label: "Expedientes", path: "/expedientes", icon: <FolderIcon /> },
        { key: "listas-trabajo", label: "Listas de Trabajo", path: "/listas-trabajo/mis-listas", icon: <PlaylistAddCheckIcon /> },
        {
            key: "mi-actividad",
            label: "Mi Actividad",
            path: "/mi-actividad",
            icon: <TimelineIcon />,
            oculto: !esAbogado,
        },
        {
            key: "bandeja-salida",
            label: "Bandeja de Salida",
            path: "/bandeja-salida",
            icon: <OutboxIcon />,
            oculto: !esAbogado,
        },
        { key: "divider2", isDivider: true },
        { key: "documentos", label: "Documentos", path: "/documentos", icon: <DescriptionIcon /> },
        {
            key: "biblioteca", label: "Biblioteca", path: "/biblioteca", icon: <LibraryBooksIcon />,
            oculto: !tienePermiso("BIBLIOTECA DE DOCUMENTOS", "SELECT")
        },
        {
            key: "custodia", label: "Custodia", path: "/custodia", icon: <Inventory2Icon />,
            oculto: !tienePermiso("CUSTODIA DE ARCHIVOS", "SELECT")
        },
        (tienePermiso("READJUDICACION MASIVA", "SELECT") || tienePermiso("CARGA MASIVA", "SELECT")) && { key: "divider3", isDivider: true },
        {
            key: "gestion-lotes",
            label: "Gestión de Lotes",
            path: "/gestion-lotes",
            icon: <LayersIcon />,
            oculto: !(tienePermiso("READJUDICACION MASIVA", "SELECT") || tienePermiso("CARGA MASIVA", "SELECT")),
        },
        {
            key: "actualizacion-dgr",
            label: "Planes de Pago DGR",
            path: "/actualizacion-dgr/planes-pago",
            icon: <ReceiptLongIcon />,
            oculto: !tienePermiso("ACTUALIZACIÓN DGR", "SELECT"),
        },
        {
            key: "conciliacion-externa",
            label: "Conciliación Externa",
            path: "/conciliacion-externa",
            icon: <AccountBalanceIcon />,
            oculto: !tienePermiso("CONCILIACIÓN BANCARIA", "SELECT"),
        },
        {
            key: "auditoria",
            label: "Auditoría",
            path: "/auditoria",
            icon: <FactCheckIcon />,
            // Acepta cualquiera de las dos funciones que el sistema viejo y el
            // nuevo usan historicamente:
            //   Id=21  "PLANES DE PAGO Y REGIMEN LEY 1504-I AUDITAR PLANES" (el viejo)
            //   Id=36  "PLANES DE PAGO - AUDITORIA / DESVINCULACION DE FORMULARIOS" (el nuevo)
            // Sin modulo a proposito para que respete el wildcard
            // "SECRETARIA GENERAL Y DAP".
            oculto: !(
                tienePermiso("PLANES DE PAGO - AUDITORIA / DESVINCULACION DE FORMULARIOS") ||
                tienePermiso("PLANES DE PAGO Y REGIMEN LEY 1504-I AUDITAR PLANES")
            ),
        },
        (tienePanelFirma || esAbogadoApoderado || firmasAbogadoPendientes > 0) && { key: "divider-panel-firma", isDivider: true },
        {
            key: "panel-firma",
            label: "Panel de Firma",
            path: "/panel-firma",
            icon: <DrawIcon />,
            oculto: !tienePanelFirma
        },
        {
            key: "panel-firma-abogado",
            label: firmasAbogadoPendientes > 0 ? `Panel de Firma (${firmasAbogadoPendientes})` : "Panel de Firma",
            path: "/panel-firma-abogado",
            icon: <DrawIcon />,
            // Visible siempre para apoderados (aunque no tengan pendientes ahora) y
            // ocultado para Fiscales/Delegados (que ya ven el otro Panel de Firma).
            oculto: tienePanelFirma || (!esAbogadoApoderado && firmasAbogadoPendientes === 0),
        },
        { key: "divider-honorarios", isDivider: true },
        {
            key: "honorarios",
            label: "Honorarios",
            path: "/honorarios",
            icon: <AttachMoneyIcon />,
            // Además del permiso, tiene que tener honorarios propios O ser abogado por
            // función (un abogado nuevo, sin certificados todavía, igual debe verlo).
            // NO alcanza con la función sola: hay 9 directores/subdirectores de área
            // que cobran honorarios y cuyo cargo no dice "abogado".
            oculto: !tienePermiso("PLANES DE PAGO Y REGIMEN LEY 1504-I CONSULTAR HONORARIOS", "SELECT", "EJECUCIONES TRIBUTARIAS Y OTROS REQUERIMIENTOS DE CREDITOS FISCALES")
                || !(tieneHonorarios || esAbogado)
        },
        tienePermiso("PLANES DE PAGO - HONORARIOS COMPARTIDOS Y EN CUOTAS (ADMIN)") && {
            key: "honorarios-admin",
            label: "Administracion",
            path: "/honorarios/admin",
            icon: <SupervisorAccountIcon />
        },
    ].filter(Boolean).filter(item => !item.oculto);

    return (
        <Drawer variant="permanent" anchor="left" classes={{ paper: classes.drawerPaper }}>
            <div className={classes.toolbar} />
            <div className={classes.logoBox}>
                <img
                    src={menuOpen ? LogoExpandido : LogoContraido}
                    alt="SIGIF Logo"
                    style={{
                        width: menuOpen ? 120 : 32,
                        height: menuOpen ? 56 : 32,
                        transition: "all .25s",
                        margin: menuOpen ? "10px auto" : "10px 0",
                        display: "block",
                        objectFit: "contain"
                    }}
                />
            </div>
            <Divider />
            <div className={classes.listScroll}>
                <List>
                    {menuStructure.map((menu) => {
                        // Si es un divider (o un false resultado de evaluación condicional)
                        if (!menu || menu.isDivider) {
                            return menu && menu.isDivider
                                ? <Divider key={menu.key} style={{ margin: "8px 0" }} />
                                : null;
                        }

                        // Oculto por permisos
                        if (menu.oculto) return null;

                        // Si tiene submenú
                        if (menu.hasSubmenu) {
                            if (!menu.submenu || menu.submenu.length === 0) return null;

                            const submenuItem = (
                                <ListItemButton onClick={() => menu.setOpen(!menu.open)}>
                                    <ListItemIcon className={classes.listItemIcon}>{menu.icon}</ListItemIcon>
                                    <ListItemText primary={menu.label} className={classes.listText} />
                                    {menuOpen && (menu.open ? <ExpandLess /> : <ExpandMore />)}
                                </ListItemButton>
                            );

                            return (
                                <React.Fragment key={menu.key}>
                                    {!menuOpen ? (
                                        <Tooltip title={menu.label} placement="right" arrow>
                                            {submenuItem}
                                        </Tooltip>
                                    ) : submenuItem}
                                    <Collapse in={menu.open && menuOpen} timeout="auto" unmountOnExit>
                                        <List component="div" disablePadding>
                                            {menu.submenu.map((subItem) => {
                                                const subMenuItem = (
                                                    <ListItemButton
                                                        key={subItem.key}
                                                        component={Link}
                                                        to={subItem.path}
                                                        className={`${classes.nested} ${location.pathname === subItem.path ? classes.active : ""}`}
                                                        selected={location.pathname === subItem.path}
                                                    >
                                                        <ListItemIcon className={classes.listItemIcon}>{subItem.icon}</ListItemIcon>
                                                        <ListItemText primary={subItem.label} className={classes.listText} />
                                                    </ListItemButton>
                                                );
                                                return !menuOpen ? (
                                                    <Tooltip key={subItem.key} title={subItem.label} placement="right" arrow>
                                                        {subMenuItem}
                                                    </Tooltip>
                                                ) : subMenuItem;
                                            })}
                                        </List>
                                    </Collapse>
                                </React.Fragment>
                            );
                        }

                        // Item normal sin submenú
                        const menuItem = (
                            <ListItemButton
                                key={menu.key}
                                component={Link}
                                to={menu.path}
                                selected={(location.pathname.startsWith(menu.path) && menu.path !== "/") || (location.pathname === menu.path)}
                                className={(location.pathname.startsWith(menu.path) && menu.path !== "/") || (location.pathname === menu.path) ? classes.active : ""}
                                onClick={() => {
                                    if (menu.key === "expedientes-legacy") {
                                        setModuloActivo && setModuloActivo(null);
                                    }
                                }}
                            >
                                <ListItemIcon className={classes.listItemIcon}>{menu.icon}</ListItemIcon>
                                <ListItemText
                                    primary={menu.label}
                                    className={classes.listText}
                                    primaryTypographyProps={{
                                        fontSize: menu.label.length > 16 ? '0.82rem' : '0.875rem',
                                        lineHeight: 1.3
                                    }}
                                />
                            </ListItemButton>
                        );

                        return !menuOpen ? (
                            <Tooltip key={menu.key} title={menu.label} placement="right" arrow>
                                {menuItem}
                            </Tooltip>
                        ) : menuItem;
                    })}
                </List>
            </div>
            <div className={classes.bottom}>
                <div>{menuOpen ? 'Fiscalía de Estado' : 'SIGIF'}</div>
                {/* Version visible. Corta a proposito: al usuario "v1.1" le alcanza para
                    reportarla; el commit y la fecha estan en el dialogo, para nosotros. */}
                {/* Si la copia cargada quedo vieja, la version se pone en naranja para
                    que se note sin tener que abrir el dialogo. De poco sirve un aviso
                    que hay que ir a buscar. */}
                <div
                    onClick={() => setAcercaDeAbierto(true)}
                    title={bundleViejo
                        ? 'Estás usando una copia vieja — clic para ver cómo actualizar'
                        : 'Ver detalle de versiones'}
                    style={{
                        fontSize: 11,
                        opacity: bundleViejo ? 1 : 0.8,
                        cursor: 'pointer',
                        marginTop: 2,
                        color: bundleViejo ? '#e65100' : undefined,
                        fontWeight: bundleViejo ? 600 : undefined,
                    }}
                >
                    {bundleViejo && '⚠ '}
                    {versionFront?.version ? `v${versionFront.version}` : 'dev'}
                </div>
            </div>
            <AcercaDeDialog
                open={acercaDeAbierto}
                onClose={() => setAcercaDeAbierto(false)}
                versionFront={versionFront}
            />
        </Drawer>
    );
};

export default Sidebar;


